import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  conversationsQueries,
  type ConversationListParams,
} from '../api/conversations-queries';
import type { Chat, ChatCountDetails, ConversationId, MacroLang } from './schema';

/**
 * Thin server-state hooks over the conversation query-options. The page reads
 * the URL (`folder`/`chat`/…) and passes it in; widgets stay presentational and
 * receive the resolved data as props.
 */

interface ConversationListView {
  /** Every loaded row, de-duplicated by id (cpanel merged pages by id). */
  rows: Chat[];
  /** Total rows the filter matches on the server. */
  total: number;
  /** Per-status totals behind the tab badges. */
  countDetails: ChatCountDetails | undefined;
}

/** Chat-list pages for the URL state; `fetchNextPage` loads the next 15. */
export function useConversations(params: ConversationListParams) {
  return useInfiniteQuery({
    ...conversationsQueries.list(params),
    select: (data): ConversationListView => {
      const seen = new Set<string>();
      const rows: Chat[] = [];
      for (const page of data.pages) {
        for (const chat of page.data) {
          const key = String(chat.id);
          if (seen.has(key)) continue;
          seen.add(key);
          rows.push(chat);
        }
      }
      const first = data.pages[0];
      return {
        rows,
        total: first?.count ?? 0,
        countDetails: first?.count_details ?? undefined,
      };
    },
  });
}

/** One chat; disabled until an id is selected. */
export function useConversation(id: ConversationId | null | undefined) {
  return useQuery({
    ...conversationsQueries.detail(id ?? ''),
    enabled: id != null && id !== '',
  });
}

/** The newest `15 × pageCount` messages; keeps the previous window while loading. */
export function useConversationMessages(
  id: ConversationId | null | undefined,
  pageCount: number,
) {
  return useQuery({
    ...conversationsQueries.messages(id ?? '', pageCount),
    enabled: id != null && id !== '',
  });
}

/** Operator-only notes of a chat. */
export function useConversationNotes(id: ConversationId | null | undefined) {
  return useQuery({
    ...conversationsQueries.notes(id ?? ''),
    // The notes endpoint is not live on the backend yet (404) — never request it.
    enabled: false,
  });
}

/** Reply templates in one language. */
export function useChatMacros(lang: MacroLang) {
  return useQuery(conversationsQueries.macros(lang));
}

/** Live presence of the chat owner (re-polled every few seconds). */
export function useChatPresence(
  userId: ConversationId | null | undefined,
  role: string | undefined,
) {
  return useQuery({
    ...conversationsQueries.presence(userId ?? '', role),
    enabled: userId != null && userId !== '',
  });
}

/** The courier snapshot for the side panel; disabled for non-courier chats. */
export function useChatCourier(courierId: ConversationId | null | undefined) {
  return useQuery({
    ...conversationsQueries.courier(courierId ?? ''),
    enabled: courierId != null && courierId !== '',
  });
}

/** Call-center operators a chat can be handed over to. */
export function useChatOperators(enabled = true) {
  return useQuery({ ...conversationsQueries.operators(), enabled });
}
